/**
 * SPA update detection. Each build stamps a `__BUILD_ID__` into the bundle and
 * emits a matching `/version.json` (served no-store). We poll that file and,
 * when the deployed id differs from the running one, surface a reload prompt so
 * users on a stale tab pick up a fresh prod deploy without a hard, data-losing
 * auto-refresh.
 */
import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n.js';
import { GlassButton } from './components.js';

declare const __BUILD_ID__: string | undefined;

/** Baked in at build time; falls back to 'dev' during local development. */
export const CURRENT_BUILD_ID = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';

async function fetchDeployedBuildId(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store', signal });
    if (!res.ok) return null;
    const data = (await res.json()) as { buildId?: string };
    return typeof data.buildId === 'string' ? data.buildId : null;
  } catch {
    return null;
  }
}

/**
 * Returns `true` once a newer build has been deployed. Checks on mount, on an
 * interval, and whenever the tab regains focus. No-ops in local dev.
 */
export function useVersionCheck(intervalMs = 60_000): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    if (CURRENT_BUILD_ID === 'dev') return;

    const controller = new AbortController();
    const check = async () => {
      if (done.current) return;
      const deployed = await fetchDeployedBuildId(controller.signal);
      if (deployed && deployed !== CURRENT_BUILD_ID) {
        done.current = true;
        setUpdateAvailable(true);
      }
    };

    const id = window.setInterval(check, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    void check();

    return () => {
      controller.abort();
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [intervalMs]);

  return updateAvailable;
}

/**
 * Floating glass banner shown when a new deploy is detected. Drop it once near
 * the root of each app. `intervalMs` controls how often the check polls.
 */
export function UpdateBanner({ intervalMs }: { intervalMs?: number }) {
  const { l } = useI18n();
  const updateAvailable = useVersionCheck(intervalMs);
  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[100] flex justify-center"
      style={{
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        paddingLeft: 'max(1rem, env(safe-area-inset-left))',
        paddingRight: 'max(1rem, env(safe-area-inset-right))',
      }}
    >
      <div className="glass-strong flex w-full max-w-xl flex-wrap items-center gap-3 rounded-3xl py-2 ps-4 pe-2 shadow-lg sm:w-auto sm:max-w-none sm:flex-nowrap sm:rounded-full">
        <span aria-hidden className="text-base">✨</span>
        <div className="min-w-0 flex-1 basis-44 leading-tight">
          <div className="text-[13px] font-bold text-ink">
            {l({ en: 'A new version is available', ar: 'يتوفر إصدار جديد' })}
          </div>
          <div className="text-[11px] text-ink-faint">
            {l({ en: 'Reload to get the latest updates.', ar: 'أعد التحميل للحصول على آخر التحديثات.' })}
          </div>
        </div>
        <GlassButton
          variant="primary"
          className="ms-auto flex-1 px-4 py-2 text-[13px] sm:flex-none"
          onClick={() => window.location.reload()}
        >
          {l({ en: 'Reload', ar: 'تحديث' })}
        </GlassButton>
      </div>
    </div>
  );
}
